import axios from 'axios';

// Declare window._env_ type
declare global {
  interface Window {
    _env_?: {
      API_URL?: string;
    };
  }
}

// Get API URL from runtime config (Docker) or build-time env or fallback to localhost
const API_BASE_URL = window._env_?.API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor for auth
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Un 401 sur /auth/login ou /auth/register veut dire « identifiants incorrects »,
    // pas « session expirée » : sans cette exception, la page était rechargée vers
    // /login et le message d'erreur du formulaire disparaissait.
    const url: string = error.config?.url ?? '';
    const isAuthAttempt = url.includes('/auth/login') || url.includes('/auth/register');
    if (error.response?.status === 401 && !isAuthAttempt) {
      if (window.location.pathname !== '/login') {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Types
export interface Product {
  id: number;
  name: string;
  description: string;
  price: number;
  purchase_price: number;
  category: string;
  unit: string;
}

export interface Supplier {
  id: number;
  name: string;
  contact_person?: string;
  email?: string;
  phone?: string;
  address?: string;
}

export interface OrderItem {
  product_id: number;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export interface OrderData {
  supplier_id: number;
  delivery_date?: string;
  items: OrderItem[];
}

export interface Order {
  id: number;
  supplier_id: number;
  order_date: string;
  delivery_date?: string;
  total_amount: number;
  status: string;
  items?: OrderItem[];
}

export interface SaleItem {
  product_id: number;
  quantity: number;
  unit_price: number;
  total_price: number;
}

export interface SaleData {
  sale_date: string;
  payment_method: string;
  items: SaleItem[];
}

export interface Sale {
  id: number;
  sale_date: string;
  timestamp: string;
  total_amount: number;
  payment_method: string;
  items: SaleItem[];
}

export interface Stock {
  id: number;
  product_id: number;
  product_name?: string;
  quantity: number;
  minimum_quantity: number;
  supplier_id?: number;
  purchase_price: number;
  total_value: number;
  last_updated: string;
}

export interface StockMovement {
  id: number;
  quantity_change: number;
  reason: string;
  created_at: string;
  user?: string;
}

export interface SaleItemDetail {
  id: number;
  product_id: number;
  product_name: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  purchase_price: number;
  item_profit: number;
}

export interface SaleDetail {
  id: number;
  sale_date: string;
  timestamp: string;
  total_amount: number;
  payment_method: string;
  profit: number;
  items: SaleItemDetail[];
}

export interface DashboardStats {
  today_sales: number;
  yesterday_sales: number;
  daily_growth: number;
  month_sales: number;
  last_month_sales: number;
  monthly_growth: number;
  month_sales_count: number;
  low_stock_alerts: number;
  total_products: number;
  pending_orders: number;
}

export interface TopProduct {
  product_id: number;
  name: string;
  category: string;
  total_quantity: number;
  total_revenue: number;
  sales_count: number;
}

export interface LowStockItem {
  product_id: number;
  name: string;
  category: string;
  current_quantity: number;
  minimum_quantity: number;
  shortage: number;
  price: number;
}

export interface CategorySales {
  category: string;
  total_amount: number;
  total_quantity: number;
  sales_count: number;
}

export interface ChartData {
  date: string;
  amount: number;
}

export interface MonthlyReport {
  month: string;
  total_sales: number;
  number_of_transactions: number;
  daily_average: number;
}

// API Functions with proper types
export const getProducts = async (): Promise<Product[]> => {
  try {
    const response = await api.get<Product[]>('/products/');
    return response.data;
  } catch (error) {
    console.error('Error fetching products:', error);
    throw error;
  }
};

export const createProduct = async (productData: Omit<Product, 'id'>): Promise<Product> => {
  try {
    const response = await api.post<Product>('/products/', productData);
    return response.data;
  } catch (error) {
    console.error('Error creating product:', error);
    throw error;
  }
};

export const getSuppliers = async (): Promise<Supplier[]> => {
  try {
    const response = await api.get<Supplier[]>('/suppliers/');
    return response.data;
  } catch (error) {
    console.error('Error fetching suppliers:', error);
    throw error;
  }
};

export const createOrder = async (orderData: OrderData): Promise<Order> => {
  try {
    const response = await api.post<Order>('/orders/', orderData);
    return response.data;
  } catch (error) {
    console.error('Error creating order:', error);
    throw error;
  }
};

export const getOrders = async (): Promise<Order[]> => {
  try {
    const response = await api.get<Order[]>('/orders/');
    return response.data;
  } catch (error) {
    console.error('Error fetching orders:', error);
    throw error;
  }
};

export const createSale = async (saleData: SaleData): Promise<Sale> => {
  try {
    const response = await api.post<Sale>('/sales/', saleData);
    return response.data;
  } catch (error) {
    console.error('Error creating sale:', error);
    throw error;
  }
};

export const getDailySales = async (date: string): Promise<Sale[]> => {
  try {
    const response = await api.get<Sale[]>(`/sales/daily/${date}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching daily sales:', error);
    throw error;
  }
};

export const getMonthlyReport = async (year: number, month: number): Promise<MonthlyReport> => {
  try {
    const response = await api.get<MonthlyReport>(`/sales/monthly-report/${year}/${month}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching monthly report:', error);
    throw error;
  }
};

export const getStocks = async (): Promise<Stock[]> => {
  try {
    const response = await api.get<Stock[]>('/stocks/');
    return response.data;
  } catch (error) {
    console.error('Error fetching stocks:', error);
    throw error;
  }
};

export const updateStock = async (productId: number, quantity: number): Promise<Stock> => {
  try {
    const response = await api.put<Stock>(`/stocks/${productId}`, { quantity });
    return response.data;
  } catch (error) {
    console.error('Error updating stock:', error);
    throw error;
  }
};

export const getDashboardStats = async (): Promise<DashboardStats> => {
  try {
    const response = await api.get<DashboardStats>('/dashboard/stats');
    return response.data;
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
};

export const getTopProducts = async (limit: number = 10, days: number = 30): Promise<TopProduct[]> => {
  try {
    const response = await api.get<TopProduct[]>(`/dashboard/top-products?limit=${limit}&days=${days}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching top products:', error);
    throw error;
  }
};

export const getLowStockDetails = async (): Promise<LowStockItem[]> => {
  try {
    const response = await api.get<LowStockItem[]>('/dashboard/low-stock-details');
    return response.data;
  } catch (error) {
    console.error('Error fetching low stock details:', error);
    throw error;
  }
};

export const getSalesByCategory = async (days: number = 30): Promise<CategorySales[]> => {
  try {
    const response = await api.get<CategorySales[]>(`/dashboard/sales-by-category?days=${days}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching sales by category:', error);
    throw error;
  }
};

export const getDashboardChartData = async (days: number = 7): Promise<ChartData[]> => {
  try {
    const response = await api.get<ChartData[]>(`/dashboard/chart-data?days=${days}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching chart data:', error);
    throw error;
  }
};

// Invoice API
export const downloadInvoicePDF = async (saleId: number): Promise<void> => {
  try {
    const response = await api.get(`/invoices/sale/${saleId}`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `facture_${saleId}.pdf`);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading invoice:', error);
    throw error;
  }
};

// Barcode API
export const getProductByBarcode = async (barcode: string): Promise<Product> => {
  try {
    const response = await api.get<Product>(`/products/barcode/${barcode}`);
    return response.data;
  } catch (error) {
    console.error('Error fetching product by barcode:', error);
    throw error;
  }
};

// Stock adjustments
export const adjustStock = async (stockId: number, quantityChange: number, reason: string): Promise<Stock> => {
  const response = await api.post<Stock>(`/stocks/${stockId}/adjust`, { quantity_change: quantityChange, reason });
  return response.data;
};

export const getStockMovements = async (stockId: number): Promise<StockMovement[]> => {
  const response = await api.get<StockMovement[]>(`/stocks/${stockId}/movements`);
  return response.data;
};

// Sales detail & cancel
export const getSaleDetail = async (saleId: number): Promise<SaleDetail> => {
  const response = await api.get<SaleDetail>(`/sales/${saleId}`);
  return response.data;
};

export const cancelSale = async (saleId: number): Promise<void> => {
  await api.delete(`/sales/${saleId}`);
};

export default api;
